import ExtraInningRuleSwitch from 'components/extraInningRuleSwitch/ExtraInningRuleSwitch';
import { ServiceTypes } from 'constants/ServiceTypes';
import { observer } from 'mobx-react-lite';
import React from 'react';
import Switch from 'react-switch';
import { FeatureConfigService } from 'services/FeatureConfigService';
import { useInjection } from 'services/ioc/ioc.react';
import { LineupStore } from 'stores/LineupStore';
import { SessionStore } from 'stores/SessionStore';
import AddNote from '../AddNote/AddNote';
import {
  ContentWrapper,
  Description,
  Headline,
  OptionWrapper,
  PlayerTrackingHint,
  PlayerTrackingWrapper,
  PreferencesWrapper,
  RecordVideo,
  StyledCheckbox,
  Title,
  VideoWrapper,
} from './styles/PreferencesStyles';
import { useVideoUploader } from './useVideoUploader';

interface IProps {
  isPrivateGame: boolean;
  isResumeGame: boolean;
}

export const Preferences: React.FC<IProps> = observer((props) => {
  const { isPrivateGame, isResumeGame } = props;
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const { videoAvailability, isVideoCaptureEnabled, videoAvailabilityLoading, handleVideoCaptureEnabled } = useVideoUploader();
  const lineupStore = useInjection<LineupStore>(ServiceTypes.LineupStore);
  const featureConfigService = useInjection<FeatureConfigService>(ServiceTypes.FeatureConfigService);

  const isVideoDisabled = (): boolean => {
    if (!featureConfigService.getConfig().isPlayerTrackingEnabled) {
      if (sessionStore.isSessionStarted || !videoAvailability?.isEnabled || videoAvailabilityLoading) {
        return true;
      }
      return false;
    }

    if (sessionStore.isSessionStarted || !videoAvailability?.isEnabled || videoAvailabilityLoading || !isPrivateGame) {
      return true;
    }

    return false;
  };

  const videoSwitchHandler = (checked: boolean) => {
    handleVideoCaptureEnabled(checked);
    lineupStore.setIsPlayerTrackingEnabled(false);
  };

  return (
    <PreferencesWrapper>
      <Headline>Optional settings</Headline>
      <OptionWrapper>
        <ContentWrapper>
          <ExtraInningRuleSwitch />
        </ContentWrapper>
        {videoAvailability && (
          <ContentWrapper>
            <VideoWrapper>
              <RecordVideo>
                <Title>{videoAvailability.isEnabled ? 'Record video' : 'Video recording not avaliable - Not enough diskspace'}</Title>
                <Description>
                  {videoAvailability.isEnabled ? (
                    <div>
                      A short video clip of each pitch will be recorded. Make sure you have{' '}
                      <span>{`${videoAvailability.minimumFreeGb} GBs`}</span> of avaliable diskspace pr. game{' '}
                    </div>
                  ) : (
                    <div>
                      <span>{`${videoAvailability.freeGb}GB`}</span> avaliable -<span>{`${videoAvailability.minimumFreeGb}GB`}</span> is
                      needed pr. game{' '}
                    </div>
                  )}
                </Description>
              </RecordVideo>
              <Switch
                checked={isVideoCaptureEnabled}
                onChange={videoSwitchHandler}
                onColor='#ec691a'
                checkedIcon={false}
                uncheckedIcon={false}
                disabled={isVideoDisabled()}
                width={51}
                height={31}
              />
            </VideoWrapper>
            <PlayerTrackingHint>Select to include:</PlayerTrackingHint>
            <PlayerTrackingWrapper>
              <StyledCheckbox
                label='Player tracking'
                width='24px'
                checked={lineupStore.isPlayerTrackingEnabled}
                onChange={(event: any) => lineupStore.setIsPlayerTrackingEnabled(event.target.checked)}
                withoutBorder
                name='playerTracking'
                id='playerTracking'
                disabled={
                  !isPrivateGame || !isVideoCaptureEnabled || isResumeGame || !featureConfigService.getConfig().isPlayerTrackingEnabled
                }
                reversed
              />
            </PlayerTrackingWrapper>
          </ContentWrapper>
        )}
      </OptionWrapper>
      <AddNote />
    </PreferencesWrapper>
  );
});
